import React, { useState, useRef } from 'react';
import { 
  PaperClipIcon, 
  XMarkIcon, 
  PhotoIcon,
  DocumentIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { TaskCommentAttachment } from '../../types/task';
import { 
  formatFileSize, 
  getFileIcon, 
  isImage,
  validateFile 
} from '../../services/taskCommentAttachmentService';
import toast from 'react-hot-toast';

interface CommentAttachmentsProps {
  attachments?: TaskCommentAttachment[];
  onDelete?: (attachmentId: string) => void;
  canDelete?: boolean;
}

export const CommentAttachmentsList: React.FC<CommentAttachmentsProps> = ({
  attachments,
  onDelete,
  canDelete = false
}) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
        >
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {isImage(attachment.type) ? (
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
              >
                <img
                  src={attachment.url}
                  alt={attachment.originalName}
                  className="h-12 w-12 object-cover rounded"
                />
              </a>
            ) : (
              <span className="text-2xl flex-shrink-0">{getFileIcon(attachment.type)}</span>
            )}
            
            <div className="flex-1 min-w-0">
              <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 truncate block"
              >
                {attachment.originalName}
              </a>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatFileSize(attachment.size)}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-1 ml-2">
            <a
              href={attachment.url}
              download={attachment.originalName}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              title="Letöltés"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </a>
            
            {canDelete && onDelete && (
              <button
                onClick={() => onDelete(attachment.id)}
                className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Törlés"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

interface CommentFileUploadProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
}

export const CommentFileUpload: React.FC<CommentFileUploadProps> = ({
  onFilesSelected,
  maxFiles = 20
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    if (files.length === 0) return;

    // Validálás
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error}`);
        continue;
      }
      validFiles.push(file);
    }

    if (selectedFiles.length + validFiles.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} fájl tölthető fel!`);
      return;
    }

    const newFiles = [...selectedFiles, ...validFiles];
    setSelectedFiles(newFiles);
    onFilesSelected(newFiles);
    
    // Input reset
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFilesSelected(newFiles);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <button
        type="button"
        onClick={handleButtonClick}
        className="inline-flex items-center px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
        disabled={selectedFiles.length >= maxFiles}
      >
        <PaperClipIcon className="h-4 w-4 mr-1.5" />
        Fájl csatolása ({selectedFiles.length}/{maxFiles})
      </button>

      {selectedFiles.length > 0 && (
        <div className="space-y-1">
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <span className="text-xl flex-shrink-0">
                  {file.type.startsWith('image/') ? '🖼️' : getFileIcon(file.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                className="p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
